import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, RotateCcw, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const INFO_FIELDS = [
  ["customer_name", "Customer Name"], ["invoice_number", "Invoice Number"], ["bill_date", "Bill Date"],
  ["branch_name", "Branch Name"], ["branch_address", "Branch Address"], ["services", "Services"],
  ["staff_name", "Staff Name"], ["total_amount", "Total Amount"], ["discount", "Discount"],
  ["payment_mode", "Payment Mode"], ["invoice_pdf", "Invoice PDF (link)"], ["invoice_link", "Invoice Link"],
];
const MSG_FIELDS = [
  ["header", "Invoice Header Message"], ["thank_you", "Thank You Message"], ["referral", "Referral Message (intro)"],
  ["booking", "Booking Message"], ["review", "Review Request Message"], ["custom", "Custom Additional Message"],
  ["footer", "Invoice Footer Message"],
];
const ORDER_LABELS = {
  header: "Header Message", thank_you: "Thank You Message", summary: "Invoice Summary",
  invoice_link: "Invoice Link", invoice_pdf: "Invoice PDF Link", referral: "Referral Code",
  booking: "Booking Link", review: "Review Link", website: "Website Link",
  custom_links: "Custom Links", custom: "Custom Message", footer: "Footer Message",
};
const PLACEHOLDERS = ["customer_name", "invoice_number", "bill_date", "branch_name", "total_amount", "payment_mode", "invoice_link", "booking_link", "review_link", "referral_code"];

const Row = ({ label, children, hint }) => (
  <div className="flex items-center justify-between gap-4 py-1">
    <span className="text-sm">{label}{hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}</span>
    {children}
  </div>
);

export function InvoiceSettings() {
  const { user } = useAuth();
  const isOwner = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const [s, setS] = useState(null);
  const [orig, setOrig] = useState(null);
  const [branches, setBranches] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/referrals/invoice-settings").then((r) => { setS(r.data); setOrig(JSON.parse(JSON.stringify(r.data))); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, [load]);

  if (!s) return <Card className="p-6 bg-card border-border">Loading invoice settings…</Card>;

  const canEdit = isOwner || s.edit_permission;
  const dirty = JSON.stringify(s) !== JSON.stringify(orig);

  const setInfo = (k, v) => setS({ ...s, info: { ...s.info, [k]: v } });
  const setRef = (k, v) => setS({ ...s, referral: { ...s.referral, [k]: v } });
  const setAction = (a, k, v) => setS({ ...s, actions: { ...s.actions, [a]: { ...s.actions[a], [k]: v } } });
  const setMsg = (k, patch) => setS({ ...s, messages: { ...s.messages, [k]: { ...s.messages[k], ...patch } } });
  const setReviewUrl = (bid, v) => setS({ ...s, review_urls: { ...(s.review_urls || {}), [bid]: v } });

  const addCustomLink = () => setS({ ...s, custom_links: [...(s.custom_links || []), { enabled: true, label: "", url: "", order: (s.custom_links || []).length }] });
  const setCustomLink = (i, patch) => setS({ ...s, custom_links: s.custom_links.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const delCustomLink = (i) => setS({ ...s, custom_links: s.custom_links.filter((_, idx) => idx !== i) });

  const moveOrder = (i, dir) => {
    const arr = [...s.order]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setS({ ...s, order: arr });
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/referrals/invoice-settings", s); setS(data); setOrig(JSON.parse(JSON.stringify(data))); toast.success("Invoice settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = () => { setS(JSON.parse(JSON.stringify(orig))); toast.message("Unsaved changes reset"); };
  const showPreview = async () => {
    try { const { data } = await api.post("/referrals/invoice-preview", { settings: s }); setPreview(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="invoice-settings">
      {!canEdit && (
        <Card className="p-4 bg-card border-border flex items-center gap-3 text-sm text-muted-foreground" data-testid="invoice-settings-locked">
          <Lock size={16} /> Only Owner / Super Admin can edit Invoice Settings. Ask the Owner to grant access.
        </Card>
      )}

      <fieldset disabled={!canEdit} className={!canEdit ? "opacity-70 pointer-events-none space-y-4" : "space-y-4"}>
        {/* Invoice Information */}
        <Card className="p-6 bg-card border-border space-y-2" data-testid="inv-info-card">
          <h3 className="font-display text-xl mb-2">Invoice Information</h3>
          <p className="text-xs text-muted-foreground mb-2">Choose what details are included with the invoice.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {INFO_FIELDS.map(([k, label]) => (
              <Row key={k} label={label}><Switch checked={!!s.info[k]} onCheckedChange={(v) => setInfo(k, v)} data-testid={`inv-info-${k}`} /></Row>
            ))}
          </div>
        </Card>

        {/* Referral Information */}
        <Card className="p-6 bg-card border-border space-y-3" data-testid="inv-referral-card">
          <h3 className="font-display text-xl">Referral Information</h3>
          <Row label="Show Referral Code"><Switch checked={!!s.referral.show_code} onCheckedChange={(v) => setRef("show_code", v)} data-testid="inv-ref-show" /></Row>
          <div><Label className="text-xs">Referral Message</Label><Input value={s.referral.message} onChange={(e) => setRef("message", e.target.value)} placeholder="Your Referral Code: {{referral_code}}" className="bg-secondary mt-1" data-testid="inv-ref-message" /></div>
          <div><Label className="text-xs">Referral Benefit / Offer Text</Label><Input value={s.referral.benefit_text} onChange={(e) => setRef("benefit_text", e.target.value)} placeholder="Share & both get 10% off!" className="bg-secondary mt-1" data-testid="inv-ref-benefit" /></div>
          <Row label="Show Referral Link"><Switch checked={!!s.referral.show_link} onCheckedChange={(v) => setRef("show_link", v)} data-testid="inv-ref-showlink" /></Row>
          {s.referral.show_link && <div><Label className="text-xs">Referral Link</Label><Input value={s.referral.link} onChange={(e) => setRef("link", e.target.value)} placeholder="https://definesalons.com/refer" className="bg-secondary mt-1" data-testid="inv-ref-link" /></div>}
          <p className="text-[11px] text-muted-foreground">Referral codes are generated automatically — this only controls how they’re shown.</p>
        </Card>

        {/* Customer Action Links */}
        <Card className="p-6 bg-card border-border space-y-4" data-testid="inv-actions-card">
          <h3 className="font-display text-xl">Customer Action Links</h3>
          {[["book_again", "Book Again / New Booking", "{{booking_link}}"], ["review", "Review Link", "{{review_link}}"], ["website", "Website Link", "https://definesalons.com"]].map(([a, title, ph]) => (
            <div key={a} className="border border-border/60 rounded-lg p-3 space-y-2">
              <Row label={title}><Switch checked={!!s.actions[a].enabled} onCheckedChange={(v) => setAction(a, "enabled", v)} data-testid={`inv-act-${a}-enabled`} /></Row>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Label</Label><Input value={s.actions[a].label} onChange={(e) => setAction(a, "label", e.target.value)} className="bg-secondary mt-1" data-testid={`inv-act-${a}-label`} /></div>
                <div><Label className="text-xs">URL</Label><Input value={s.actions[a].url} onChange={(e) => setAction(a, "url", e.target.value)} placeholder={ph} className="bg-secondary mt-1" data-testid={`inv-act-${a}-url`} /></div>
              </div>
            </div>
          ))}
          <div>
            <Label className="text-xs">Google Review URL per branch (used for that branch’s invoices)</Label>
            {branches.map((b) => (
              <div key={b.id} className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-muted-foreground w-40 truncate">{b.name}</span>
                <Input value={(s.review_urls || {})[b.id] || ""} onChange={(e) => setReviewUrl(b.id, e.target.value)} placeholder="https://g.page/r/..." className="bg-secondary flex-1" data-testid={`inv-review-url-${b.id}`} />
              </div>
            ))}
          </div>
        </Card>

        {/* Custom Links */}
        <Card className="p-6 bg-card border-border space-y-3" data-testid="inv-customlinks-card">
          <div className="flex items-center justify-between"><h3 className="font-display text-xl">Custom Links</h3>
            <Button size="sm" variant="outline" onClick={addCustomLink} className="rounded-sm" data-testid="inv-customlink-add"><Plus size={14} className="mr-1" /> Add Link</Button></div>
          {(s.custom_links || []).length === 0 && <p className="text-xs text-muted-foreground">No custom links yet.</p>}
          {(s.custom_links || []).map((c, i) => (
            <div key={i} className="border border-border/60 rounded-lg p-3 space-y-2" data-testid={`inv-customlink-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><Switch checked={!!c.enabled} onCheckedChange={(v) => setCustomLink(i, { enabled: v })} data-testid={`inv-customlink-${i}-enabled`} /><span className="text-xs text-muted-foreground">Enabled</span></div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Order</Label>
                  <Input type="number" value={c.order ?? i} onChange={(e) => setCustomLink(i, { order: Number(e.target.value) })} className="w-16 bg-secondary h-8" data-testid={`inv-customlink-${i}-order`} />
                  <Button size="icon" variant="ghost" onClick={() => delCustomLink(i)} data-testid={`inv-customlink-${i}-del`}><Trash2 size={15} className="text-destructive" /></Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input value={c.label} onChange={(e) => setCustomLink(i, { label: e.target.value })} placeholder="View Our Latest Offers" className="bg-secondary" data-testid={`inv-customlink-${i}-label`} />
                <Input value={c.url} onChange={(e) => setCustomLink(i, { url: e.target.value })} placeholder="https://…" className="bg-secondary" data-testid={`inv-customlink-${i}-url`} />
              </div>
            </div>
          ))}
        </Card>

        {/* Custom Invoice Messages */}
        <Card className="p-6 bg-card border-border space-y-3" data-testid="inv-messages-card">
          <h3 className="font-display text-xl">Custom Invoice Messages</h3>
          {MSG_FIELDS.map(([k, label]) => (
            <div key={k} className="border border-border/60 rounded-lg p-3">
              <Row label={label}><Switch checked={!!s.messages[k]?.enabled} onCheckedChange={(v) => setMsg(k, { enabled: v })} data-testid={`inv-msg-${k}-enabled`} /></Row>
              <Textarea value={s.messages[k]?.text || ""} onChange={(e) => setMsg(k, { text: e.target.value })} rows={2} className="bg-secondary mt-1" placeholder="Message text… (placeholders allowed)" data-testid={`inv-msg-${k}-text`} />
            </div>
          ))}
        </Card>

        {/* WhatsApp Message Formats */}
        <Card className="p-6 bg-card border-border space-y-3" data-testid="inv-format-card">
          <h3 className="font-display text-xl">WhatsApp Invoice Message Format</h3>
          <p className="text-xs text-muted-foreground">Leave blank to auto-build from the sections & order above. Fill in to fully customise a format. Placeholders: {PLACEHOLDERS.map((p) => `{{${p}}}`).join(", ")}</p>
          <div><Label className="text-xs">Normal WhatsApp format (optional override)</Label><Textarea value={s.wa_normal_format} onChange={(e) => setS({ ...s, wa_normal_format: e.target.value })} rows={3} className="bg-secondary mt-1" data-testid="inv-format-normal" /></div>
          <div><Label className="text-xs">WhatsApp API format (optional override)</Label><Textarea value={s.wa_api_format} onChange={(e) => setS({ ...s, wa_api_format: e.target.value })} rows={3} className="bg-secondary mt-1" data-testid="inv-format-api" /></div>
        </Card>

        {/* Message Order */}
        <Card className="p-6 bg-card border-border space-y-2" data-testid="inv-order-card">
          <h3 className="font-display text-xl mb-1">Message Order</h3>
          <p className="text-xs text-muted-foreground mb-2">Control the order enabled items appear in the message.</p>
          {s.order.map((key, i) => (
            <div key={key} className="flex items-center justify-between border border-border/60 rounded-lg px-3 py-2" data-testid={`inv-order-${key}`}>
              <span className="text-sm"><span className="text-muted-foreground mr-2">{i + 1}.</span>{ORDER_LABELS[key] || key}</span>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => moveOrder(i, -1)} disabled={i === 0} data-testid={`inv-order-${key}-up`}><ArrowUp size={15} /></Button>
                <Button size="icon" variant="ghost" onClick={() => moveOrder(i, 1)} disabled={i === s.order.length - 1} data-testid={`inv-order-${key}-down`}><ArrowDown size={15} /></Button>
              </div>
            </div>
          ))}
        </Card>

        {/* Permissions (Owner only) */}
        {isOwner && (
          <Card className="p-6 bg-card border-border" data-testid="inv-permission-card">
            <Row label="Allow Managers & Reception to edit Invoice Settings" hint="Off by default — only Owner/Super Admin can edit.">
              <Switch checked={!!s.edit_permission} onCheckedChange={(v) => setS({ ...s, edit_permission: v })} data-testid="inv-edit-permission" />
            </Row>
          </Card>
        )}
      </fieldset>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 sticky bottom-2">
        <Button onClick={save} disabled={!canEdit || saving || !dirty} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="inv-save"><Save size={15} className="mr-1" /> {saving ? "Saving…" : "Save Settings"}</Button>
        <Button variant="outline" onClick={reset} disabled={!dirty} className="rounded-sm" data-testid="inv-reset"><RotateCcw size={15} className="mr-1" /> Reset Unsaved Changes</Button>
        <Button variant="outline" onClick={showPreview} className="rounded-sm" data-testid="inv-preview-btn"><Eye size={15} className="mr-1" /> Preview Invoice Message</Button>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inv-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Invoice Message Preview</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-2">Normal WhatsApp</p>
              <pre className="text-xs whitespace-pre-wrap bg-secondary rounded-lg p-3 border border-border" data-testid="inv-preview-normal">{preview?.normal || "(empty)"}</pre>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">WhatsApp API</p>
              <pre className="text-xs whitespace-pre-wrap bg-secondary rounded-lg p-3 border border-border" data-testid="inv-preview-api">{preview?.api || "(empty)"}</pre>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Preview uses sample data. Actual invoices use the real customer & bill details.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
